import { useState } from 'react';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  url: string;
}

const shareTargets = [
  { key: 'wechat', label: '微信', icon: 'ri-wechat-line', className: 'bg-secondary-100 text-secondary-800' },
  { key: 'weibo', label: '微博', icon: 'ri-weibo-line', className: 'bg-accent-100 text-accent-800' },
  { key: 'qq', label: 'QQ', icon: 'ri-qq-line', className: 'bg-primary-100 text-primary-700' },
  { key: 'link', label: '复制链接', icon: 'ri-link', className: 'bg-background-200 text-foreground-700' },
];

export default function ShareModal({ open, onClose, title, url }: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [hint, setHint] = useState('');

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setHint('链接已复制到剪贴板');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setHint('复制失败，请手动复制地址栏链接');
    }
  };

  const handleTarget = (key: string) => {
    if (key === 'link') {
      handleCopy();
      return;
    }
    setHint(`已模拟分享至${shareTargets.find((t) => t.key === key)?.label}（演示功能）`);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      onClick={onClose}
      role="presentation"
    >
      <div className="absolute inset-0 bg-primary-950/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-lg bg-background-50 p-6 md:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-foreground-950">分享影视</h3>
            <p className="mt-1 text-sm text-foreground-500 line-clamp-1">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer shrink-0"
            aria-label="关闭"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-4 gap-4">
          {shareTargets.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => handleTarget(t.key)}
              className="flex flex-col items-center gap-2 cursor-pointer group"
            >
              <span
                className={`w-12 h-12 rounded-full flex items-center justify-center ${t.className} group-hover:scale-105 transition-transform`}
              >
                <i className={`${t.icon} text-xl`} />
              </span>
              <span className="text-xs text-foreground-600">{t.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-2 p-3 rounded-md bg-background-100 border border-background-200/70">
          <i className="ri-links-line text-foreground-400" />
          <span className="flex-1 text-xs text-foreground-600 truncate">{url}</span>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 h-8 px-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium whitespace-nowrap transition-colors cursor-pointer"
          >
            {copied ? '已复制' : '复制'}
          </button>
        </div>

        {hint && <p className="mt-3 text-xs text-accent-700">{hint}</p>}
        <p className="mt-3 text-xs text-foreground-400">
          提示：分享功能为前端演示，不会真正发布到社交平台。
        </p>
      </div>
    </div>
  );
}